import {
  Box,
  Flex,
  Image,
  Input,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import {
  AllOrderedProduct,
  AllOrderedProductOption,
  AllOrderedProductsOrder,
} from "@/features/line-sheet-sets/hooks/use-get-boutique-all-ordered-products";
import {
  displayLineSheetName,
  PriceAdjustmentDraft,
  previewConfirmedPrice,
  sanitizePercentInput,
  sumProduct,
} from "@/features/line-sheet-sets/all-ordered-products/all-ordered-products-helpers";
import { ProductPrice } from "@/features/types";
import { isPriceAdjustedBy } from "@/features/line-sheet-sets/line-sheet-set.types";

// 라인시트 카드 안 오더시트 표와 같은 색 규칙 — 주문은 초록, 확정은 파랑
const ORDERED_COLOR = "#008040";
const CONFIRMED_COLOR = "#1272EF";
const MUTED_COLOR = "#8F8F8C";
const LINE_COLOR = "#EFEFED";

const cellProps = {
  padding: "10px 8px",
  fontSize: "12px",
  borderBottom: `0.5px solid ${LINE_COLOR}`,
  verticalAlign: "top",
} as const;

const headerCellProps = {
  ...cellProps,
  position: "sticky",
  top: 0,
  zIndex: 1,
  background: "#FCFCFB",
  color: "#000",
  fontWeight: "700",
  textTransform: "none",
  letterSpacing: "normal",
  lineHeight: "16px",
  verticalAlign: "middle",
} as const;

interface AllOrderedProductsTableProps {
  products: AllOrderedProduct[];
  lineSheetNameById: Map<number, string | null>;
  // 라인시트 하나만 보고 있을 땐 라인시트 칸이 전부 같은 값이라 감춘다
  showLineSheet: boolean;
  // 주문 한 건을 볼 때만 — 옵션마다 확정 수량을 입력한다. 없으면 조회 전용이다.
  confirmInput?: {
    // 입력 중인 값(없으면 저장된 확정 수량, 그것도 없으면 주문 수량을 보여준다)
    draftOf: (
      product: AllOrderedProduct,
      option: AllOrderedProductOption
    ) => number | undefined;
    onChange: (
      product: AllOrderedProduct,
      option: AllOrderedProductOption,
      value: number
    ) => void;
    // 확정 가격조정 — 입력 중인 값(없으면 오더에 저장된 확정 가격조정)
    priceDraftOf: (
      product: AllOrderedProduct
    ) => PriceAdjustmentDraft | undefined;
    onPriceChange: (
      product: AllOrderedProduct,
      draft: PriceAdjustmentDraft
    ) => void;
    // 확정 단가 미리보기에 쓰는 세트 통화·수수료
    order: AllOrderedProductsOrder;
    isDisabled?: boolean;
  };
}

// 입력 중인 확정 가격조정 → 없으면 오더에 저장된 확정 가격조정
function currentPriceDraft(
  product: AllOrderedProduct,
  confirmInput: NonNullable<AllOrderedProductsTableProps["confirmInput"]>
): PriceAdjustmentDraft | undefined {
  const draft = confirmInput.priceDraftOf(product);
  if (draft) {
    return draft;
  }
  const saved = product.orderPrice?.priceAdjustedBy;
  return saved ? { by: saved.by, value: Number(saved.value) || 0 } : undefined;
}

export function confirmDraftKey(
  product: AllOrderedProduct,
  option: AllOrderedProductOption
) {
  return `${product.id}_${option.name}`;
}

function OptionCell({
  product,
  option,
  confirmInput,
}: {
  product: AllOrderedProduct;
  option: AllOrderedProductOption;
  confirmInput?: AllOrderedProductsTableProps["confirmInput"];
}) {
  const { t } = useI18nHelper();
  const draft = confirmInput?.draftOf(product, option);
  return (
    <Flex
      data-testid={"all_ordered_option"}
      flexDirection={"column"}
      alignItems={"center"}
      minWidth={"52px"}
      padding={"4px 6px"}
      border={`0.5px solid ${LINE_COLOR}`}
      borderRadius={"4px"}
      gap={"2px"}
    >
      <Text fontSize={"11px"} color={"#6F6F6C"}>
        {option.name || "-"}
      </Text>
      <Text fontSize={"13px"} fontWeight={"700"} color={ORDERED_COLOR}>
        {option.quantity.toLocaleString()}
      </Text>
      {confirmInput ? (
        <Input
          aria-label={`confirmed_quantity_${option.name}`}
          size={"xs"}
          width={"48px"}
          textAlign={"center"}
          inputMode={"numeric"}
          color={CONFIRMED_COLOR}
          isDisabled={confirmInput.isDisabled}
          value={draft ?? option.confirmedQuantity ?? option.quantity}
          onChange={(event) => {
            // 숫자만 받고, 주문 수량보다 많이 확정할 수는 없다
            const digits = event.target.value.replace(/\D/g, "");
            const value = Math.min(Number(digits || 0), option.quantity);
            confirmInput.onChange(product, option, value);
          }}
        />
      ) : (
        <Text
          fontSize={"11px"}
          color={
            option.confirmedQuantity === null ? MUTED_COLOR : CONFIRMED_COLOR
          }
          whiteSpace={"nowrap"}
        >
          {option.confirmedQuantity === null
            ? t("unconfirmed")
            : option.confirmedQuantity.toLocaleString()}
        </Text>
      )}
    </Flex>
  );
}

// 라인시트 상품 화면의 가격 칸과 같은 순서 — 공급가(굵게) / 도매가 / 소매가.
// 오더 한 건을 볼 때는 맨 위에 그 오더의 확정가(가격조정을 바꾸면 미리보기)를 더한다.
function PriceCell({
  product,
  confirmInput,
}: {
  product: AllOrderedProduct;
  confirmInput?: AllOrderedProductsTableProps["confirmInput"];
}) {
  const { tTitle, tCurrencyString } = useI18nHelper();
  const prices: [string, ProductPrice | null][] = [
    ["supply_price", product.supplyPrice],
    ["cost", product.costPrice],
    ["retail", product.retailPrice],
  ];
  if (product.orderPrice) {
    const draft = confirmInput?.priceDraftOf(product);
    const amount =
      draft && confirmInput
        ? previewConfirmedPrice(product, draft, confirmInput.order)
        : product.orderPrice.price?.amount ?? null;
    prices.unshift([
      "all_ordered_products.confirmed_price",
      amount === null
        ? null
        : {
            amount,
            // 세트 통화가 먼저다(오더시트 로더는 확정가 통화를 EUR 로 고정해 준다)
            currency:
              confirmInput?.order.currency ??
              product.orderPrice.price?.currency ??
              "EUR",
          },
    ]);
  }
  return (
    <>
      {prices.map(([label, price], index) => (
        <Flex
          key={label}
          justifyContent={"space-between"}
          gap={"8px"}
          whiteSpace={"nowrap"}
        >
          <Text color={MUTED_COLOR} fontSize={"11px"}>
            {tTitle(label)}
          </Text>
          <Text
            data-testid={`price_${label}`}
            fontWeight={index === 0 ? "700" : "400"}
            color={index === 0 ? "#303136" : "#54565F"}
          >
            {price?.amount
              ? tCurrencyString(price.amount, {
                  type: "SYMBOL",
                  code: price.currency,
                })
              : "-"}
          </Text>
        </Flex>
      ))}
    </>
  );
}

const capitalize = (value: string) =>
  value.substring(0, 1) + value.substring(1).toLowerCase();

// 라인시트 상품 화면의 가격조정 칸과 같은 표기 — Markup / Discount + %.
// 오더 한 건을 볼 때는 그 오더의 확정 가격조정을 보여주고, 확정할 수 있으면 고칠 수 있다.
function PriceAdjustedByCell({
  product,
  confirmInput,
}: {
  product: AllOrderedProduct;
  confirmInput?: AllOrderedProductsTableProps["confirmInput"];
}) {
  if (confirmInput) {
    const draft = currentPriceDraft(product, confirmInput);
    if (!draft) {
      return <Text color={MUTED_COLOR}>-</Text>;
    }
    return (
      <Flex flexDirection={"column"} gap={"4px"}>
        <Select
          aria-label={`price_adjusted_by_${product.id}`}
          size={"xs"}
          width={"92px"}
          isDisabled={confirmInput.isDisabled}
          value={draft.by}
          onChange={(event) =>
            confirmInput.onPriceChange(product, {
              ...draft,
              by: event.target.value as PriceAdjustmentDraft["by"],
            })
          }
        >
          {(["MARKUP", "DISCOUNT"] as const).map((by) => (
            <option key={by} value={by}>
              {capitalize(by)}
            </option>
          ))}
        </Select>
        <Flex alignItems={"center"} gap={"2px"}>
          <Input
            aria-label={`price_adjusted_value_${product.id}`}
            size={"xs"}
            width={"60px"}
            textAlign={"end"}
            inputMode={"decimal"}
            isDisabled={confirmInput.isDisabled}
            value={draft.input ?? draft.value}
            onChange={(event) => {
              // 숫자와 소수점 하나만 받는다. 친 글자는 그대로 두고 계산·저장은 숫자로 한다.
              const input = sanitizePercentInput(event.target.value);
              confirmInput.onPriceChange(product, {
                ...draft,
                input,
                value: parseFloat(input) || 0,
              });
            }}
          />
          <Text color={"#54565F"}>%</Text>
        </Flex>
      </Flex>
    );
  }
  const adjusted =
    product.orderPrice?.priceAdjustedBy ?? product.priceAdjustedBy;
  if (!isPriceAdjustedBy(adjusted)) {
    return <Text color={MUTED_COLOR}>-</Text>;
  }
  return (
    <>
      <Text fontWeight={"700"} color={"#303136"}>
        {capitalize(adjusted.by)}
      </Text>
      <Text color={"#54565F"}>{`${adjusted.value}%`}</Text>
    </>
  );
}

/**
 * (부티크) 세트에 들어온 모든 주문을 상품 한 줄로 합쳐 보여주는 표. 조회 전용이다 —
 * 확정 수량은 바이어 주문별 오더시트 상세에서 넣고, 여기에는 그 합만 보인다.
 * 옵션 칸: 사이즈 / 주문 합계(초록) / 확정 합계(파랑, 없으면 미확정)
 * 가격·가격조정은 세트 전체를 볼 때는 라인시트 상품 그대로다(세트 취합이라 바이어별 오더 조건을 씌우지 않는다).
 * 오더 한 건을 볼 때는 그 오더의 확정가·확정 가격조정을 보여주고, 확정할 수 있으면 가격조정도 고친다.
 */
export default function AllOrderedProductsTable({
  products,
  lineSheetNameById,
  showLineSheet,
  confirmInput,
}: AllOrderedProductsTableProps) {
  const { t, tTitle } = useI18nHelper();

  return (
    <Table variant={"unstyled"} width={"100%"}>
      <Thead>
        <Tr>
          <Th {...headerCellProps} width={"64px"}>
            {tTitle("image")}
          </Th>
          <Th {...headerCellProps} minWidth={"200px"}>
            {tTitle("all_ordered_products.product")}
          </Th>
          {showLineSheet && (
            <Th {...headerCellProps} minWidth={"140px"}>
              {tTitle("line_sheet")}
            </Th>
          )}
          <Th {...headerCellProps} width={"150px"}>
            {tTitle("price")}
          </Th>
          <Th {...headerCellProps} width={"90px"}>
            {tTitle("all_ordered_products.price_adjusted_by")}
          </Th>
          <Th {...headerCellProps}>
            {tTitle("all_ordered_products.quantity_by_option")}
          </Th>
          <Th {...headerCellProps} isNumeric width={"90px"}>
            {tTitle("ordered_quantity")}
          </Th>
          <Th {...headerCellProps} isNumeric width={"90px"}>
            {tTitle("confirmed_quantity")}
          </Th>
        </Tr>
      </Thead>
      <Tbody>
        {products.map((product) => {
          const total = sumProduct(product);
          const imageUrl =
            product.imageUrls?.[0] ?? product.alternativeImageUrls?.[0];
          return (
            <Tr key={product.id} data-testid={"all_ordered_product_row"}>
              <Td {...cellProps}>
                {imageUrl ? (
                  <Image
                    src={imageUrl}
                    alt={product.modelNumber ?? ""}
                    boxSize={"48px"}
                    objectFit={"contain"}
                    loading={"lazy"}
                    background={"#FFF"}
                    border={`0.5px solid ${LINE_COLOR}`}
                    borderRadius={"4px"}
                  />
                ) : (
                  <Box
                    boxSize={"48px"}
                    background={"#F7F7F5"}
                    borderRadius={"4px"}
                  />
                )}
              </Td>
              <Td {...cellProps}>
                <Text fontWeight={"700"}>{product.brandName}</Text>
                <Text>{product.modelNumber || product.sku || "-"}</Text>
                <Text color={"#6F6F6C"}>
                  {[product.name, product.colorName || product.colorCode]
                    .filter(Boolean)
                    .join(" · ")}
                </Text>
              </Td>
              {showLineSheet && (
                <Td {...cellProps} color={"#6F6F6C"}>
                  {displayLineSheetName(
                    lineSheetNameById.get(product.lineSheetId)
                  )}
                </Td>
              )}
              <Td {...cellProps} data-testid={"all_ordered_price"}>
                <PriceCell product={product} confirmInput={confirmInput} />
              </Td>
              <Td {...cellProps} data-testid={"all_ordered_price_adjusted_by"}>
                <PriceAdjustedByCell
                  product={product}
                  confirmInput={confirmInput}
                />
              </Td>
              <Td {...cellProps}>
                <Flex gap={"4px"} wrap={"wrap"}>
                  {product.orderQuantityWithOptionList.map((option) => (
                    <OptionCell
                      key={`${product.id}_${option.name}`}
                      product={product}
                      option={option}
                      confirmInput={confirmInput}
                    />
                  ))}
                </Flex>
              </Td>
              <Td {...cellProps} isNumeric>
                <Text fontWeight={"700"} color={ORDERED_COLOR}>
                  {`${total.quantity.toLocaleString()}${t("quantity")}`}
                </Text>
              </Td>
              <Td
                {...cellProps}
                isNumeric
                fontWeight={"700"}
                color={
                  total.confirmedQuantity === null
                    ? MUTED_COLOR
                    : CONFIRMED_COLOR
                }
              >
                {total.confirmedQuantity === null
                  ? t("unconfirmed")
                  : `${total.confirmedQuantity.toLocaleString()}${t(
                      "quantity"
                    )}`}
              </Td>
            </Tr>
          );
        })}
      </Tbody>
    </Table>
  );
}
